import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="new-event"
export default class extends Controller {
  private isContentValid = false;
  private isContentSameAsIA = false;

  static values = {
    aiQuotaReached: Boolean,
    aiLimit: Number,
  };

  static targets = [
    "btnImproveIA",
    "btnIA",
    "descriptionIA",
    "textarea",
    "acceptIA",
    "refuseIA",
    "errorIA",
    "spanInfo",
    "textIA",
    "textEnabled",
  ];

  declare btnImproveIATarget: HTMLElement;
  declare btnIATarget: HTMLElement;
  declare descriptionIATarget: HTMLElement;
  declare textareaTarget: HTMLTextAreaElement;
  declare acceptIATarget: HTMLElement;
  declare refuseIATarget: HTMLElement;
  declare errorIATarget: HTMLElement;
  declare spanInfoTarget: HTMLSpanElement;
  declare textIATarget: HTMLElement;
  declare descriptionIAValue: string;
  declare textEnabledTarget: HTMLElement;
  declare aiQuotaReachedValue: boolean;
  declare aiLimitValue: number;

  connect() {
    this.acceptIATarget.addEventListener("click", this.acceptIA.bind(this));
    this.refuseIATarget.addEventListener("click", this.refuseIA.bind(this));
    this.btnImproveIATarget.addEventListener("click", this.showIA.bind(this));
    this.errorIATarget.addEventListener("click", this.errorIA.bind(this));

    this.textareaTarget.addEventListener("trix-initialize", () => {
      this.initState();
    });
  }

  initState() {
    const content = (this.textareaTarget.value || "").trim();
    this.isContentValid = content.length >= 100;

    this.isContentSameAsIA =
      this.normalize(this.textareaTarget.value) === this.normalize(this.descriptionIAValue || "");

    this.setImproveButtonState(this.isContentValid, this.isContentSameAsIA);
  }

  acceptIA() {
    this.descriptionIATarget.classList.add("hidden");
    this.btnIATarget.classList.add("hidden");

    this.btnImproveIATarget.classList.remove("hidden");

    this.textareaTarget.value = this.descriptionIAValue;
    this.textareaTarget.focus();

    this.toggleButton();
  }

  refuseIA() {
    this.descriptionIATarget.classList.add("hidden");
    this.btnIATarget.classList.add("hidden");
    this.btnImproveIATarget.classList.remove("hidden");

    this.textareaTarget.focus();

    this.toggleButton();
  }

  async showIA() {
    if (!this.isContentValid || this.isContentSameAsIA || this.aiQuotaReachedValue) return;

    this.spanInfoTarget.classList.remove("group-hover:block");
    const content = (this.textareaTarget.value || "").trim();

    this.descriptionIATarget.classList.remove("hidden");
    this.btnImproveIATarget.classList.add("hidden");

    try {
      this.textIATarget.innerHTML = `
      <div class="m-5 grid grid-cols-3 gap-4 animate-pulse">
        <div class="h-3 col-span-2 rounded bg-gray-200"></div>
        <div class="h-3 rounded col-span-1 bg-gray-200"></div>
        <div class="h-3 rounded col-span-3 bg-gray-200"></div>
        <div class="h-3 rounded col-span-1 bg-gray-200"></div>
        <div class="h-3 rounded col-span-2 bg-gray-200"></div>
        <div class="h-3 rounded col-span-3 bg-gray-200"></div>
        <div class="h-3 col-span-2 rounded bg-gray-200"></div>
        <div class="h-3 rounded col-span-1 bg-gray-200"></div>
        <div class="h-3 rounded col-span-1 bg-gray-200"></div>
        <div class="h-3 rounded col-span-2 bg-gray-200"></div>
        <div class="h-3 rounded col-span-3 bg-gray-200"></div>
      </div>
    `;
      const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
      if (!meta) {
        throw new Error("CSRF token not found");
      }
      const token = meta.content;
      const response = await fetch("/events/ai_suggest", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": token,
        },
        body: JSON.stringify({ text: content }),
      });

      if (!response.ok) {
        let message = "Erreur lors de la génération par IA.";

        try {
          const errorData = await response.json();
          message = errorData.error || message;
        } catch (_) {}

        throw new Error(message);
      }

      const data = await response.json();

      this.aiQuotaReachedValue = data.ai_requests_count >= this.aiLimitValue;
      this.setImproveButtonState(this.isContentValid, this.isContentSameAsIA);

      this.textIATarget.innerHTML = data.html;
      this.descriptionIAValue = data.html;

      if (
        this.descriptionIATarget.textContent.includes(
          "Veuillez décrire votre évènement afin qu’il puisse être reformulé correctement",
        )
      ) {
        this.errorIATarget.classList.remove("hidden");
      } else {
        this.btnIATarget.classList.remove("hidden");
      }
    } catch (e: any) {
      console.error(e);
      this.textIATarget.innerHTML = `<div class='flex flex-row gap-2 text-red-500'><svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='1.5' stroke='currentColor' class='h-5 w-5'><path stroke-linecap='round' stroke-linejoin='round' d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z'/></svg><p>${e.message}</p></div>`;
      this.errorIATarget.classList.remove("hidden");
    }
  }

  errorIA() {
    this.descriptionIATarget.classList.add("hidden");
    this.btnImproveIATarget.classList.remove("hidden");
    this.errorIATarget.classList.add("hidden");
    this.textareaTarget.focus();

    this.toggleButton();
  }

  toggleButton() {
    const content = this.textareaTarget.value.trim();
    this.isContentValid = content.length >= 100;

    this.isContentSameAsIA =
      this.normalize(this.textareaTarget.value) === this.normalize(this.descriptionIAValue || "");

    this.setImproveButtonState(this.isContentValid, this.isContentSameAsIA);
  }

  setImproveButtonState(contentValid: boolean, contentSame: boolean) {
    const el = this.btnImproveIATarget;
    const quotaReached = this.aiQuotaReachedValue;
    const enabled = contentValid && !contentSame && !quotaReached ? true : false;

    const active = [
      "border-primary-light",
      "text-primary",
      "hover:bg-gradient-to-r",
      "hover:from-primary-darker",
      "hover:to-primary-light",
      "hover:text-white",
      "hover:border-none",
      "cursor-pointer",
      "hover:py-[5px]",
      "hover:px-[9px]",
    ];
    const inactive = ["border-gray-500", "text-gray-500", "bg-gray-200"];

    el.classList.remove(...active, ...inactive);
    el.classList.add(...(enabled ? active : inactive));

    if (quotaReached) {
      this.spanInfoTarget.classList.add("group-hover:block");
      this.textEnabledTarget.innerHTML = "<p>Vous avez atteint votre quota quotidien d'améliorations IA.</p>";
    } else if (!contentValid) {
      this.spanInfoTarget.classList.add("group-hover:block");
      this.textEnabledTarget.innerHTML = "<p>Minimum 100 caractères requis pour utiliser l'IA.</p>";
    } else if (contentSame) {
      this.spanInfoTarget.classList.add("group-hover:block");
      this.textEnabledTarget.innerHTML =
        "<p>Votre texte a déjà été généré par l'IA.<br>Pour réutiliser cette fonctionnalité, veuillez effectuer des modifications.</p>";
    } else {
      this.textEnabledTarget.innerHTML = "";
      this.spanInfoTarget.classList.remove("group-hover:block");
    }
  }

  normalize(text: string): string {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = text;

    return textarea.value
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, "")
      .trim()
      .toLowerCase();
  }
}
